"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnnouncementsBanner = AnnouncementsBanner;
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const react_redux_1 = require("react-redux");
const announcements_1 = require("~/store/game/announcements");
const creatureDefinitions_1 = require("~/networking/creatureDefinitions");
const useStyles_1 = require("~/useStyles");
const LevelIcon_1 = require("../ui/icon/LevelIcon");
const streakIndicator_1 = require("./playerList/streakIndicator");
// Continuous ticker speed in px/s.
const SCROLL_SPEED = 90;
// Time in ms for the fade-in/out of the bar itself.
const FADE_MS = 320;
// Minimum gap between consecutive messages (px).
const MIN_BUFFER = 56;
const useStyles = (0, useStyles_1.createUseThemeStyles)((theme) => ({
    banner: {
        position: "relative",
        width: "100%",
        // Fixed height keeps the bar constant regardless of message content (no
        // flicker) and, on the board, reserves layout space so it never shifts.
        height: "1.95rem",
        overflow: "hidden",
        background: `color-mix(in oklab, ${theme.palette.light.neutral} 4%, ${theme.palette.surface})`,
        borderTop: `1px solid color-mix(in oklab, ${theme.palette.accent.neutral} 22%, transparent)`,
        borderBottom: `1px solid color-mix(in oklab, ${theme.palette.light.neutral} 8%, transparent)`,
        fontFamily: theme.typography.primary,
        userSelect: "none",
        opacity: 0,
    },
    // On the overlays we don't reserve space; the bar collapses when idle and
    // pushes the overlay content down when a message is showing.
    collapsed: {
        height: 0,
    },
    item: {
        // Each message is positioned independently; the per-frame loop sets its
        // translateX. left:0 + top/bottom:0 centres content vertically.
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.95rem",
        whiteSpace: "nowrap",
        willChange: "transform",
    },
    player: {
        color: theme.palette.primary.light,
        fontWeight: 700,
    },
    creature: {
        color: theme.palette.light.neutral,
        marginLeft: "0.4rem",
    },
    stars: {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        marginLeft: "0.45rem",
    },
    star: {
        height: "0.9em",
        width: "auto",
        display: "block",
    },
    levelIcon: {
        marginLeft: "0.4rem",
    },
    indicator: {
        marginLeft: "0.4rem",
    },
    eliminated: {
        color: theme.palette.muted,
        marginLeft: "0.4rem",
        fontStyle: "italic",
    },
}));
function AnnouncementBody({ announcement, classes, }) {
    var _a, _b;
    switch (announcement.kind) {
        case "upgrade":
            return (React.createElement(React.Fragment, null,
                React.createElement("span", { className: classes.creature }, (_b = (_a = (0, creatureDefinitions_1.getCreatureDefinitionById)(announcement.definitionId)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : ""),
                React.createElement("span", { className: classes.stars }, Array.from({ length: announcement.stage + 1 }, (_, i) => (React.createElement("img", { key: i, className: classes.star, src: `${APP_IMAGE_ROOT}/ui/star.svg`, alt: "" }))))));
        case "levelUp":
            return (React.createElement(LevelIcon_1.LevelIcon, { amount: announcement.level, className: classes.levelIcon }));
        case "streak":
            return (React.createElement(streakIndicator_1.StreakIndicator, { type: announcement.streakType, amount: announcement.streakAmount, className: classes.indicator }));
        case "eliminated":
            return React.createElement("span", { className: classes.eliminated }, "Eliminated");
        default:
            return null;
    }
}
function AnnouncementsBanner() {
    const announcements = (0, react_redux_1.useSelector)(announcements_1.selectAnnouncements);
    const players = (0, react_redux_1.useSelector)((state) => state.game.players);
    const onBoard = (0, react_redux_1.useSelector)((state) => state.game.ui.currentOverlay === null);
    const dispatch = (0, react_redux_1.useDispatch)();
    const styles = useStyles();
    const nameById = React.useMemo(() => {
        const map = new Map();
        for (const player of players) {
            map.set(player.id, player.name);
        }
        return map;
    }, [players]);
    const bannerRef = React.useRef(null);
    // Animation bookkeeping kept in refs so the per-frame loop never re-renders.
    const itemsRef = React.useRef(announcements);
    itemsRef.current = announcements;
    const scrollX = React.useRef(0);
    const lastTs = React.useRef(null);
    const rafId = React.useRef(null);
    // Per-message absolute position (left edge) in virtual scroll space, plus its
    // measured width. Screen x = startOffset - scrollX. Keyed by announcement id.
    const layoutRef = React.useRef(new Map());
    const fadeRef = React.useRef(null);
    const visibleRef = React.useRef(false);
    // Whether the bar occupies layout space. On overlays this stays true through
    // the fade-out (so the fade is visible), then collapses instantly.
    const [occupying, setOccupying] = React.useState(false);
    const step = React.useCallback((ts) => {
        const banner = bannerRef.current;
        const list = itemsRef.current;
        if (list.length === 0 || !banner) {
            rafId.current = null;
            lastTs.current = null;
            scrollX.current = 0;
            layoutRef.current.clear();
            return;
        }
        if (lastTs.current === null) {
            lastTs.current = ts;
        }
        const dt = Math.min((ts - lastTs.current) / 1000, 0.05);
        lastTs.current = ts;
        scrollX.current += SCROLL_SPEED * dt;
        const layout = layoutRef.current;
        // Position each message independently from its own start offset.
        for (let i = 0; i < list.length; i++) {
            const entry = layout.get(list[i].id);
            const child = banner.children[i];
            if (!entry || !child) {
                continue;
            }
            child.style.transform = `translateX(${entry.startOffset - scrollX.current}px)`;
        }
        // The head is leftmost (smallest offset); dismiss it once fully off-left.
        const head = layout.get(list[0].id);
        if (head && head.startOffset + head.width - scrollX.current <= 0) {
            dispatch((0, announcements_1.dismissAnnouncement)(list[0].id));
        }
        rafId.current = requestAnimationFrame(step);
    }, [dispatch]);
    const hasItems = announcements.length > 0;
    // Drive the ticker whenever there is something queued.
    React.useEffect(() => {
        if (hasItems && rafId.current === null) {
            lastTs.current = null;
            rafId.current = requestAnimationFrame(step);
        }
    }, [hasItems, step]);
    React.useEffect(() => () => {
        if (rafId.current !== null) {
            cancelAnimationFrame(rafId.current);
        }
    }, []);
    // Assign a start offset to each new message (and drop departed ones) before
    // the browser paints, so each enters from the right with dynamic spacing.
    React.useLayoutEffect(() => {
        const banner = bannerRef.current;
        if (!banner) {
            return;
        }
        const layout = layoutRef.current;
        const viewportWidth = banner.clientWidth;
        const presentIds = new Set(announcements.map((a) => a.id));
        for (const id of layout.keys()) {
            if (!presentIds.has(id)) {
                layout.delete(id);
            }
        }
        for (let i = 0; i < announcements.length; i++) {
            const { id } = announcements[i];
            const child = banner.children[i];
            if (!child || layout.has(id)) {
                continue;
            }
            const width = child.offsetWidth;
            const prev = i > 0 ? layout.get(announcements[i - 1].id) : undefined;
            // Start at the right edge, or further right so it clears the previous
            // message by at least MIN_BUFFER — whichever is further right.
            const startOffset = prev
                ? Math.max(viewportWidth + scrollX.current, prev.startOffset + prev.width + MIN_BUFFER)
                : viewportWidth + scrollX.current;
            layout.set(id, { startOffset, width });
            // Position immediately so the first paint isn't at the left edge.
            child.style.transform = `translateX(${startOffset - scrollX.current}px)`;
        }
    }, [announcements]);
    // Fade the bar's opacity in/out. The bar stays mounted at a fixed height so the surrounding layout never shifts.
    React.useEffect(() => {
        var _a;
        const el = bannerRef.current;
        if (!el || hasItems === visibleRef.current) {
            return;
        }
        visibleRef.current = hasItems;
        // Expand immediately on appear, collapse only once the fade-out completes.
        if (hasItems) {
            setOccupying(true);
        }
        if (typeof el.animate !== "function") {
            el.style.opacity = hasItems ? "1" : "0";
            setOccupying(hasItems);
            return;
        }
        (_a = fadeRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        const anim = el.animate(hasItems ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, easing: "ease", fill: "forwards" });
        fadeRef.current = anim;
        if (!hasItems) {
            anim.onfinish = () => setOccupying(false);
        }
    }, [hasItems]);
    const expanded = onBoard || occupying;
    return (React.createElement("div", { ref: bannerRef, className: (0, classnames_1.default)(styles.banner, { [styles.collapsed]: !expanded }), role: "status", "aria-live": "polite" }, announcements.map((announcement) => {
        var _a;
        return (React.createElement("span", { className: styles.item, key: announcement.id },
            React.createElement("span", { className: styles.player }, (_a = nameById.get(announcement.playerId)) !== null && _a !== void 0 ? _a : "Unknown",
                ":"),
            React.createElement(AnnouncementBody, { announcement: announcement, classes: styles })));
    })));
}
